// src/layouts/CustomMenu.tsx
//
// v3 "3c hybrid" rail: solid desaturated teal background (palette.background.menu),
// white/translucent-white items, entries grouped under section labels, brand
// text lockup at the top ("Roommatik" + product name — the only place the
// product name appears), a white 2px marker on the active item and the profile
// card fixed at the bottom of the rail. Pattern A: the rail spans the full
// viewport height, flush to the edges (the app bar is absolute over the
// content column — see CustomAppBar/CustomLayout).
import React from 'react';
import { useTranslate, useSidebarState, useGetIdentity } from 'react-admin';
import { Box, Typography, useMediaQuery, MenuItem as MuiMenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';

import { DashboardRounded as DashboardIcon } from '@mui/icons-material';
import { getMenuItems } from '../config/resourceConfig';
import { ProfileRailCard } from './ProfileRailCard';

/** Rail groups (3c hybrid): resource name → group. Items without a mapping
 *  fall into the trailing unlabelled group so new resources never disappear.
 *  Exported for the app bar's breadcrumb. */
export const MENU_GROUPS: { key: string; labelKey: string | null; members: string[] }[] = [
    { key: 'general', labelKey: 'shell.menu.groups.general', members: ['dashboard'] },
    { key: 'operation', labelKey: 'shell.menu.groups.operation', members: ['locker-assignments', 'lockers'] },
    { key: 'administration', labelKey: 'shell.menu.groups.administration', members: ['users', 'devices', 'admin-options'] },
    { key: 'tools', labelKey: 'shell.menu.groups.tools', members: ['hmi-simulator'] },
    { key: 'other', labelKey: null, members: [] },
];

interface StyledMenuItemProps {
  open: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
    shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuItemProps>(({ theme, open }) => ({
    position: 'relative',
    margin: theme.custom.menu.item.margin,
    padding: theme.custom.menu.item.padding,
    borderRadius: theme.custom.menu.item.borderRadius,
    color: theme.custom.menu.item.color(theme),
    justifyContent: open ? 'flex-start' : 'center',
    fontSize: '13.5px',
    '& .MuiListItemText-root': {
        minWidth: 0,
    },
    '& .MuiListItemText-primary': {
        fontSize: '13.5px',
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },

    display: 'flex',
    gap: theme.custom.menu.item.gap,
    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',

    '& .MuiListItemIcon-root': {
        minWidth: 'auto',
        color: theme.custom.menu.item.iconColor(theme),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    '&:hover:not(.active)': {
        color: theme.custom.menu.item.hover.color(theme),
        backgroundColor: open ? theme.custom.menu.item.hover.backgroundColor(theme) : 'transparent',
        ...(!open && {
            '& .MuiListItemIcon-root': {
                backgroundColor: theme.custom.menu.item.hover.backgroundColor(theme),
            },
        }),
    },

    '&.active': {
        color: theme.custom.menu.item.active.color(theme),
        backgroundColor: open ? theme.custom.menu.item.active.backgroundColor(theme) : 'transparent',
        fontWeight: theme.custom.menu.item.active.fontWeight,

        // 2px marker on the leading edge of the active item.
        '&::before': open
            ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 6,
                  bottom: 6,
                  width: 2,
                  borderRadius: '0 2px 2px 0',
                  backgroundColor: theme.custom.menu.item.active.marker(theme),
              }
            : undefined,

        '& .MuiListItemIcon-root': {
            color: open ? theme.custom.menu.item.active.marker(theme) : theme.custom.menu.item.closedActiveIcon.color(theme),
            backgroundColor: open ? 'transparent' : theme.custom.menu.item.closedActiveIcon.backgroundColor(theme),
            padding: open ? 0 : '6px',
            borderRadius: open ? 0 : (theme.custom.menu.item.closedActiveIcon.borderRadius || '6px'),
        },

        '&:hover': {
            backgroundColor: open ? theme.custom.menu.item.active.hover.backgroundColor(theme) : 'transparent',
        },
    },
}));

export const CustomMenu: React.FC = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [isSidebarOpen, setSidebarOpen] = useSidebarState();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Obtener identidad del usuario para permisos
    const { data: identity, isLoading } = useGetIdentity();

    const getTranslationKey = (itemName: string): string => {
        const translationMap: Record<string, string> = {
            'lockers': 'resources.lockers.name',
            'users': 'resources.users.name',
            'assignments': 'resources.locker-assignments.name',
            'hotels': 'resources.hotels.name',
            'zones': 'resources.zones.name',
            'groups': 'resources.groups.name',
            'machines': 'resources.machines.name',
            'reports': 'resources.reports.name',
            'settings': 'resources.settings.name',
            'products': 'resources.products.name',
            'admin-options': 'resources.adminOptions.name',
            'devices': 'resources.devices.name',
        };
        return translationMap[itemName] || `resources.${itemName}.name`;
    };

    const dashboardItem = {
        name: 'dashboard',
        to: '/',
        icon: <DashboardIcon />,
        textKey: 'ra.page.dashboard',
    };

    const menuItems = getMenuItems()
        .filter(item => item.name !== 'dashboard')
        .map(item => ({
            name: item.name,
            to: item.route,
            icon: item.icon,
            textKey: getTranslationKey(item.name),
        }));

    const isDeveloper = identity?.role === 'Developer';
    const allItems = isDeveloper ? menuItems : [dashboardItem, ...menuItems];

    // Distribute the visible items into the rail groups, preserving each
    // group's declared order; unmapped items fall into the trailing group.
    const groupedItems = React.useMemo(() => {
        const mapped = new Set(MENU_GROUPS.flatMap(g => g.members));
        return MENU_GROUPS.map(group => ({
            ...group,
            items: group.key === 'other'
                ? allItems.filter(item => !mapped.has(item.name))
                : group.members
                      .map(name => allItems.find(item => item.name === name))
                      .filter((item): item is typeof allItems[number] => Boolean(item)),
        })).filter(group => group.items.length > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allItems.map(i => i.name).join(',')]);

    const isActiveRoute = (to: string): boolean => {
        // Check exact match or if path starts with the route (for nested pages)
        if (location.pathname === to) return true;
        // For grouped pages, check if we're on a nested route
        if (to === '/admin-options') {
            return ['/groups', '/hotels', '/tag-types', '/locker-types'].some(
                path => location.pathname.startsWith(path)
            );
        }
        if (to === '/devices') {
            return ['/locker-controllers', '/hmis', '/hmi-staff'].some(
                path => location.pathname.startsWith(path)
            );
        }
        if (to !== '/') return location.pathname.startsWith(`${to}/`);
        return false;
    };

    const handleMenuClick = (to: string): void => {
        navigate(to);
        if (isSmallScreen) {
            setSidebarOpen(false);
        }
    };

    const railSx = {
        display: 'flex',
        flexDirection: 'column',
        // Patrón A: el rail ocupa TODA la altura del viewport, a ras de los
        // bordes. La AppBar es absoluta sobre la columna de contenido.
        height: '100vh',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.menu,
        borderRight: isSmallScreen ? 'none' : `1px solid ${theme.custom.menu.border}`,
        width: isSmallScreen ? '94%' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
        minWidth: isSmallScreen ? 'auto' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
        maxWidth: isSmallScreen ? '100%' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
        transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out, padding 0.2s ease-in-out',
        overflow: 'hidden',
    } as const;

    if (isLoading) {
        return (
            <Box sx={{ ...railSx, padding: '12px', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {translate('ra.action.loading', 'Cargando...')}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ ...railSx, padding: isSmallScreen ? '12px' : (isSidebarOpen ? '16px 14px 16px 8px' : '16px 6px') }}>
            {(isSidebarOpen || isSmallScreen) && (
                <Box sx={{ padding: '4px 8px 20px' }}>
                    {/* v3 "1a" brand: a text lockup on the rail — the product name
                        appears only here (no logotype in-app, no app-bar wordmark).
                        «Roommatik» solid white 800 + product name translucent
                        white 400, left-aligned on the teal rail. */}
                    <Typography
                        noWrap
                        sx={{
                            fontSize: '1.15rem',
                            lineHeight: 1.15,
                            letterSpacing: '-0.01em',
                            color: '#FFFFFF',
                            fontWeight: 800,
                        }}
                    >
                        Roommatik
                    </Typography>
                    <Typography
                        noWrap
                        sx={{
                            fontSize: '1.15rem',
                            lineHeight: 1.15,
                            letterSpacing: '-0.01em',
                            color: 'rgba(255,255,255,0.72)',
                            fontWeight: 400,
                        }}
                    >
                        Locker Management
                    </Typography>
                </Box>
            )}

            <Box sx={{ flex: 1, overflowY: isSidebarOpen || isSmallScreen ? 'auto' : 'hidden', overflowX: 'hidden' }}>
                {groupedItems.map(group => (
                    <Box key={group.key} sx={{ mb: 1 }}>
                        {group.labelKey && (isSidebarOpen || isSmallScreen) && (
                            <Typography
                                sx={{
                                    fontSize: '10.5px',
                                    fontWeight: 600,
                                    letterSpacing: '0.07em',
                                    textTransform: 'uppercase',
                                    color: theme.custom.menu.groupLabelColor,
                                    padding: '8px 8px 4px',
                                }}
                            >
                                {translate(group.labelKey)}
                            </Typography>
                        )}
                        {group.items.map(item => (
                            <StyledMenuItem
                                key={item.name}
                                onClick={() => handleMenuClick(item.to)}
                                open={isSidebarOpen}
                                className={isActiveRoute(item.to) ? 'active' : ''}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                {(isSidebarOpen || isSmallScreen) && (
                                    <ListItemText
                                        primary={translate(item.textKey, { smart_count: 1 })}
                                        primaryTypographyProps={{ noWrap: true }}
                                    />
                                )}
                            </StyledMenuItem>
                        ))}
                    </Box>
                ))}
            </Box>

            {/* v3 rule: profile card anchors the bottom of the rail (replaces
                the app-bar user menu — a single place for the profile). */}
            {(isSidebarOpen || isSmallScreen) && <ProfileRailCard />}
        </Box>
    );
};

export default CustomMenu;
